import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { toast } from 'sonner';

const AgendasPage = () => {
  const { isAdmin } = useAuth();
  const [agendas, setAgendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAgenda, setSelectedAgenda] = useState(null);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  useEffect(() => {
    loadAgendas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    findCurrentMonthAgenda();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendas, currentDate]);

  const loadAgendas = async () => {
    try {
      setLoading(true);
      const data = await api.getAgendas();
      setAgendas(data);
    } catch (error) {
      toast.error('Erro ao carregar agendas');
    } finally {
      setLoading(false);
    }
  };

  const findCurrentMonthAgenda = () => {
    const agenda = agendas.find(
      (a) => a.year === currentYear && a.month === currentMonth
    );
    setSelectedAgenda(agenda || null);
  };

  const handleCreateAgenda = async () => {
    try {
      await api.createAgenda({
        year: currentYear,
        month: currentMonth,
        config: {
          events: [],
          holidays: [],
          notes: '',
        },
      });
      toast.success('Agenda criada com sucesso');
      setDialogOpen(false);
      loadAgendas();
    } catch (error) {
      toast.error(error.message || 'Erro ao criar agenda');
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getDaysInMonth = () => {
    return new Date(currentYear, currentMonth, 0).getDate();
  };

  const getFirstDayOfMonth = () => {
    return new Date(currentYear, currentMonth - 1, 1).getDay();
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth();
    const firstDay = getFirstDayOfMonth();
    const days = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, isCurrentMonth: false });
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({ day, isCurrentMonth: true });
    }

    return days;
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth === today.getMonth() + 1 &&
      currentYear === today.getFullYear()
    );
  };

  const calendarDays = generateCalendarDays();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="agendas-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold">Agenda</h1>
          <p className="text-muted-foreground text-sm">Organize eventos e compromissos</p>
        </div>
        {isAdmin() && !selectedAgenda && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="create-agenda-button">
                <Plus className="w-4 h-4 mr-2" />
                Criar Agenda
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Agenda</DialogTitle>
                <DialogDescription>
                  Criar agenda para {months[currentMonth - 1]} de {currentYear}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Você está criando uma nova agenda para o mês atual. Após criar, você poderá
                  adicionar eventos e feriados.
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button className="flex-1" onClick={handleCreateAgenda}>
                    Criar Agenda
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Calendar Navigation */}
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="pb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigateMonth(-1)}
              data-testid="prev-month"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-center">
              <CardTitle className="text-xl">
                {months[currentMonth - 1]} {currentYear}
              </CardTitle>
              {selectedAgenda && (
                <CardDescription className="mt-0.5">
                  <Badge variant="outline" className="text-xs">Agenda criada</Badge>
                </CardDescription>
              )}
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigateMonth(1)}
              data-testid="next-month"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col min-h-0 pb-4 overflow-hidden">
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 flex-1" style={{ gridTemplateRows: 'auto repeat(6, minmax(0, 1fr))' }}>
            {/* Week day headers */}
            {weekDays.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-semibold text-muted-foreground py-1"
              >
                {day}
              </div>
            ))}

            {/* Calendar days */}
            {calendarDays.map((item, index) => (
              <div
                key={index}
                className={`
                  rounded border flex items-center justify-center
                  transition-colors min-h-0
                  ${item.day ? 'hover:bg-accent cursor-pointer p-1' : 'bg-muted/30'}
                  ${isToday(item.day) ? 'bg-primary text-primary-foreground font-bold' : ''}
                `}
                data-testid={item.day ? `day-${item.day}` : undefined}
              >
                {item.day && (
                  <span className="text-xs">{item.day}</span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AgendasPage;
